import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Menu, X, ChevronDown, Smartphone, Globe, Cloud, Zap, Users, LineChart, Factory, Lightbulb } from 'lucide-react';
import Button from '@/components/ui/Button';
import { useLocation } from 'react-router-dom';
import ServicesDropdown from './ServicesDropdown';
import { AnimatePresence, motion } from 'framer-motion';
import { COMPANY_INFO } from '@/lib/constants';

const lightTextPages = ['/ia-automatisation', '/engagement-environnemental', '/partenaires', '/blog/comment-construire-une-startup-tech-qui-dure']; 

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const shouldUseLightText = lightTextPages.includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const getTextColor = () => {
    if (isScrolled) {
      return "text-gray-700";
    }
    return shouldUseLightText ? "text-white" : "text-slate-900";
  };

  const getLogoColor = () => {
    if (isScrolled) {
      return "text-orcsys-500";
    }
    return shouldUseLightText ? "text-white" : "text-slate-900";
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-[9995] transition-all duration-300',
        isScrolled
          ? 'bg-white shadow-sm py-3'
          : 'bg-transparent py-5'
      )}
    >
      <div className={cn(
        'container mx-auto px-4 md:px-6',
        isScrolled ? 'backdrop-blur-md' : ''
      )}>
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center">
            <img src="/icon.png" alt="Orcsys" className="w-10 h-10 mr-2" />
            <span className={cn(
              "text-2xl font-bold",
              getLogoColor()
            )}>{COMPANY_INFO.name}</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <a href="/a-propos" className={cn(
              "animated-link text-sm font-medium",
              getTextColor()
            )}>À propos</a>
            <ServicesDropdown textColor={getTextColor()} />
            <a href="/projets" className={cn(
              "animated-link text-sm font-medium",
              getTextColor()
            )}>Nos projets</a>
            <a href="/collab" className={cn(
              "animated-link text-sm font-medium",
              getTextColor()
            )}>Collab</a>
            <a href="/engagement-environnemental" className={cn(
              "animated-link text-sm font-medium",
              getTextColor()
            )}>Notre engagement</a>
            {/* <a href="/idea-factory" className={cn(
              "animated-link text-sm font-medium group",
              getTextColor()
            )}>
              <span className="flex items-center">
                Idea Factory 
                <Factory className="w-4 h-4 ml-1 group-hover:animate-pulse" />
              </span>
            </a> */}
 {/*            <a href="/contact" className={cn(
              "animated-link text-sm font-medium",
              getTextColor()
            )}>Contact</a> */}
            <Button.Link
              href="/contact"
              size="sm"
              className={cn(
                isScrolled
                  ? "bg-orcsys-500 hover:bg-orcsys-600 text-white"
                  : shouldUseLightText
                    ? "bg-white text-slate-900 hover:bg-white/90"
                    : "bg-orcsys-500 hover:bg-orcsys-600 text-white"
              )}
            >
              Prendre rendez-vous
            </Button.Link>
          </nav>

          {/* Mobile menu button */}
          <button
            className={cn(
              "md:hidden p-2 rounded-md",
              isScrolled
                ? "text-gray-700 hover:bg-gray-100"
                : shouldUseLightText
                  ? "text-white hover:bg-white/10"
                  : "text-slate-900 hover:bg-slate-100"
            )}
            onClick={toggleMobileMenu}
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={cn(
          'fixed inset-0 bg-white z-[9999] transform transition-transform duration-300 ease-in-out',
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Close button */}
        <button
          className="absolute top-4 right-4 p-2 rounded-md text-slate-900 hover:bg-slate-100"
          onClick={toggleMobileMenu}
          aria-label="Fermer le menu"
        >
          <X size={24} />
        </button>

        <nav className="flex flex-col p-4 pt-20">
          <div className="w-full max-w-sm mx-auto space-y-4">
            <a
              href="/a-propos"
              className="block w-full p-3 text-lg font-medium text-slate-900 text-center rounded-lg hover:bg-slate-100 transition-colors duration-200"
              onClick={toggleMobileMenu}
            >
              À propos
            </a>
            {/* Mobile Services Dropdown */}
            <div className="w-full">
              <button
                className="w-full flex items-center justify-center space-x-2 text-lg font-medium text-slate-900 p-3 rounded-lg hover:bg-slate-100 transition-colors duration-200"
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              >
                <span>Nos services</span>
                <ChevronDown
                  className={cn(
                    "w-5 h-5 transition-transform duration-200",
                    mobileServicesOpen ? "rotate-180" : ""
                  )}
                />
              </button>
              <AnimatePresence>
                {mobileServicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-3 grid gap-2 px-1">
                      <a
                        href="/developpement-mobile"
                        className="flex items-center p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                        onClick={toggleMobileMenu}
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-orcsys-50 flex items-center justify-center text-orcsys-500">
                          <Smartphone className="w-4 h-4" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-slate-900">Applications Mobiles</p>
                          <p className="text-xs text-slate-500">Apps natives et cross-platform</p>
                        </div>
                      </a>
                      <a
                        href="/developpement-web"
                        className="flex items-center p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                        onClick={toggleMobileMenu}
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-orcsys-50 flex items-center justify-center text-orcsys-500">
                          <Globe className="w-4 h-4" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-slate-900">Applications Web</p>
                          <p className="text-xs text-slate-500">Solutions web modernes</p>
                        </div>
                      </a>
                      <a
                        href="/developpement-saas"
                        className="flex items-center p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                        onClick={toggleMobileMenu}
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-orcsys-50 flex items-center justify-center text-orcsys-500">
                          <Cloud className="w-4 h-4" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-slate-900">Développement SaaS</p>
                          <p className="text-xs text-slate-500">Solutions cloud évolutives</p>
                        </div>
                      </a>
                      <a
                        href="/ia-automatisation"
                        className="flex items-center p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                        onClick={toggleMobileMenu}
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-orcsys-50 flex items-center justify-center text-orcsys-500">
                          <Zap className="w-4 h-4" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-slate-900">Automatisation</p>
                          <p className="text-xs text-slate-500">Optimisation des processus</p>
                        </div>
                      </a>
                      <a
                        href="/developpement-marketplace"
                        className="flex items-center p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                        onClick={toggleMobileMenu}
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-orcsys-50 flex items-center justify-center text-orcsys-500">
                          <Users className="w-4 h-4" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-slate-900">Marketplaces</p>
                          <p className="text-xs text-slate-500">Plateformes de mise en relation</p>
                        </div>
                      </a>
                      <a
                        href="/maintenance-support"
                        className="flex items-center p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                        onClick={toggleMobileMenu}
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-orcsys-50 flex items-center justify-center text-orcsys-500">
                          <LineChart className="w-4 h-4" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-slate-900">Maintenance & Support</p>
                          <p className="text-xs text-slate-500">Support technique et monitoring</p>
                        </div>
                      </a>
                      <a
                        href="/prototype"
                        className="flex items-center p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                        onClick={toggleMobileMenu}
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-orcsys-50 flex items-center justify-center text-orcsys-500">
                          <Lightbulb className="w-4 h-4" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-slate-900">Prototype</p>
                          <p className="text-xs text-slate-500">Testez votre idée avant d'investir</p>
                        </div>
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a
              href="/projets"
              className="block w-full p-3 text-lg font-medium text-slate-900 text-center rounded-lg hover:bg-slate-100 transition-colors duration-200"
              onClick={toggleMobileMenu}
            >
              Nos projets
            </a>
            <a
              href="/engagement-environnemental"
              className="block w-full p-3 text-lg font-medium text-slate-900 text-center rounded-lg hover:bg-slate-100 transition-colors duration-200"
              onClick={toggleMobileMenu}
            >
              Notre engagement
            </a>
            <a
              href="/collab"
              className="block w-full p-3 text-lg font-medium text-slate-900 text-center rounded-lg hover:bg-slate-100 transition-colors duration-200"
              onClick={toggleMobileMenu}
            >
              Collab
            </a>
            {/* <a
              href="/idea-factory"
              className="block w-full p-3 text-lg font-medium text-slate-900 text-center rounded-lg hover:bg-slate-100 transition-colors duration-200 flex justify-center items-center gap-2"
              onClick={toggleMobileMenu}
            >
              Idea Factory
              <Factory className="w-5 h-5" />
            </a> */}
            {/* <a
              href="/contact"
              className="block w-full p-3 text-lg font-medium text-slate-900 text-center rounded-lg hover:bg-slate-100 transition-colors duration-200"
              onClick={toggleMobileMenu}
            >
              Contact
            </a> */}
            <Button
              href="/contact"
              onClick={toggleMobileMenu}
              className="w-full justify-center p-3 text-lg font-medium"
            >
              Prendre rendez-vous
            </Button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
